import { brandColors } from '@src/assets/colors/brandColors';
import SvgIconQuotation from '@src/assets/images/common/icon/editor/SvgIconQuotation';
import type { Editor } from '@tiptap/react';
import type { EditorState, Transaction } from 'prosemirror-state';
import React from 'react';

import {
  applyBlockquoteWithMerging,
  liftBlockquoteAtSelection,
  unsetBlockquoteForSelection,
  unsetBlockquoteForSelectionPartially,
} from '../../../utils/blockquoteUtils';

interface BlockquoteButtonProps {
  editor: Editor;
  isActive: boolean;
  refreshActiveState: () => void;
}

export const BlockquoteButton: React.FC<BlockquoteButtonProps> = ({
  editor,
  isActive,
  refreshActiveState,
}) => {
  const activeHoverColor = brandColors['500'] || '#2B5CDD';
  const defaultIconColor = 'currentColor';

  const isParagraphInsideBlockquote = (position: number, state: EditorState): boolean => {
    const resolvedPosition = state.doc.resolve(position);

    for (let depth = resolvedPosition.depth; depth > 0; depth--) {
      if (resolvedPosition.node(depth).type === state.schema.nodes.blockquote) {
        return true;
      }
    }

    return false;
  };

  const analyzeSelectionContent = (state: EditorState) => {
    const { from, to } = state.selection;
    const blockquotePositions: number[] = [];
    let hasContentOutsideBlockquote = false;

    state.doc.nodesBetween(from, to, (node, position) => {
      if (node.type === state.schema.nodes.blockquote) {
        blockquotePositions.push(position);
        return;
      }

      if (node.type === state.schema.nodes.paragraph) {
        if (!isParagraphInsideBlockquote(position, state)) {
          hasContentOutsideBlockquote = true;
        }
      }
    });

    return {
      blockquotePositions,
      hasContentOutsideBlockquote,
      hasBlockquotes: blockquotePositions.length > 0,
    };
  };

  const removeBlockquotesPartially = (
    state: EditorState,
    dispatch: ((tr: Transaction) => void) | undefined,
  ) => {
    unsetBlockquoteForSelectionPartially(state, dispatch);
    refreshActiveState();
  };

  const removeBlockquotesAndApplyToAll = () => {
    const { from, to } = editor.state.selection;

    editor
      .chain()
      .focus()
      .command(({ tr, state: commandState }) => {
        const unsetTransaction = unsetBlockquoteForSelection(commandState, undefined, tr);
        if (unsetTransaction && unsetTransaction !== true) {
          tr = unsetTransaction;
        }
        return true;
      })
      .setTextSelection({ from, to })
      .toggleBlockquote()
      .run();

    refreshActiveState();
  };

  const applyBlockquote = () => {
    const { state, view } = editor;
    
    // 隣接blockquoteとのマージを試行
    if (applyBlockquoteWithMerging(state, view.dispatch)) {
      refreshActiveState();
      return;
    }
    
    // マージできない場合は通常のblockquote適用
    editor.chain().toggleBlockquote().run();
    refreshActiveState();
  };

  const handleBlockquote = () => {
    editor.view.dom.focus({ preventScroll: true });

    const { state, view } = editor;
    const { empty } = state.selection;

    // Handle empty selection inside blockquote
    if (empty && editor.isActive('blockquote')) {
      liftBlockquoteAtSelection(state, view.dispatch);
      refreshActiveState();
      return;
    }

    const { hasBlockquotes, hasContentOutsideBlockquote } = analyzeSelectionContent(state);

    // No blockquotes in selection - apply blockquote
    if (!hasBlockquotes) {
      applyBlockquote();
      return;
    }

    // Selection contains only blockquotes - remove them partially
    if (!hasContentOutsideBlockquote) {
      removeBlockquotesPartially(state, view.dispatch);
      return;
    }

    // Mixed content - normalize by removing blockquotes and applying to all
    removeBlockquotesAndApplyToAll();
  };

  return (
    <button
      onClick={handleBlockquote}
      className={`toolbar-button ${isActive ? 'active' : ''}`}
      title='引用'
      type='button'
    >
      <SvgIconQuotation color={isActive ? activeHoverColor : defaultIconColor} />
    </button>
  );
};
